/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useIsPremiumJSW } from '@jira/navigation-apps__atlassian-navigation/src/controllers/edition/index.tsx';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.heading',
	defaultMessage: 'Jira',
	description: 'Navigation group heading for Jira settings items',
});

const configurationItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.configuration-item',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Jira configuration',
	description: 'Navigation item which links to a Jira configuration settings page',
});

const storeManagementItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.store-management-item',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Storage management',
	description: 'Navigation item which links to a Jira storage management page',
});

const onboardingHubItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.onboarding-hub-item',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Custom onboarding',
	description: 'Navigation item which links to the Onboarding Hub page',
});

const newLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.new-label',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'New',
	description: 'Lozenge marking the menu item as new item',
});

const premiumLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.premium-label',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Premium',
	description: 'Lozenge marking the menu item as premium item',
});

const JiraSection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const isPremium = useIsPremiumJSW();
	const customOnboardinLozengeMessage = !isPremium ? premiumLabel : newLabel;
	const shouldShowLozenge = !isPremium || fg('onboarding-hub-new-lozenge');

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'jiraConfiguration' }}
					isSelected={currentRoute.name === 'products-jsw-configuration'}
					href="/jira/settings/products/jira-software-configuration"
				>
					{formatMessage(configurationItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'storeManagement' }}
					isSelected={currentRoute.name === 'products-jsw-storage-management'}
					href="/jira/settings/products/jira-software-configuration/storage-management"
				>
					{formatMessage(storeManagementItem)}
				</LinkMenuItem>
				{/*
				This gate is setup to be enabled till Feb 28th 2026 (3 months from when the feature goes live to RT customers in Nov 2025).
				Please refrain from cleaning this up till then
				 */}
				{fg('jira-onboarding-hub-gate') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'onboardingHub' }}
						isSelected={[
							'products-onboarding-hub',
							'products-onboarding-hub-create',
							'products-onboarding-hub-edit',
						].includes(currentRoute.name)}
						href="/jira/settings/products/onboarding"
						elemAfter={
							shouldShowLozenge ? (
								<Lozenge appearance="new">{formatMessage(customOnboardinLozengeMessage)}</Lozenge>
							) : null
						}
					>
						{formatMessage(onboardingHubItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

const ExportedComponent: React.MemoExoticComponent<() => React.JSX.Element> = memo(JiraSection);
export default ExportedComponent;
